import { useEffect } from "react";
import { useFormContext, type FieldErrors } from "react-hook-form";
import type { DeckDesignerConfigurationFormValues } from "../../../http/deckDesigner/useAutoSaveDeckDesignerConfiguration";
import type { ScrewFieldName } from "./useSuggestedScrew";

const SCREW_COMPATIBILITY_ERROR_TYPE = "screwCompatibility";

// Owns the field's "no compatible screw" error: the screw fields have no rules, so they are written
// without shouldValidate (validating them would only clear the error and cost a re-render). error is
// the field's own error, not `errors`: RHF mutates `errors` in place, so an `errors` dependency never
// re-runs and a cleared "no compatible screw" error would be lost for good.
export const useScrewCompatibilityError = (
  name: ScrewFieldName,
  hasNoCompatibleScrew: boolean,
  message: string,
  error: FieldErrors<DeckDesignerConfigurationFormValues>[ScrewFieldName],
): void => {
  const { setError, clearErrors, trigger } = useFormContext<DeckDesignerConfigurationFormValues>();

  useEffect(() => {
    if (hasNoCompatibleScrew) {
      if (error?.type !== SCREW_COMPATIBILITY_ERROR_TYPE || error.message !== message) {
        setError(name, { type: SCREW_COMPATIBILITY_ERROR_TYPE, message });
      }

      return;
    }

    if (error?.type === SCREW_COMPATIBILITY_ERROR_TYPE) {
      clearErrors(name);
      // setError turned the form's isValid off and clearErrors doesn't turn it back on - autosave would
      // wait for the next validating change. Validating this rule-less field recomputes isValid without
      // touching the other fields' errors (what the old validated screw writes did as a side effect).
      void trigger(name);
    }
  }, [clearErrors, error, hasNoCompatibleScrew, message, name, setError, trigger]);
};
